import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CapacityPlanningInputService } from '../../service/capacity-planning-input.service';
import { catchError, concatMap, forkJoin, from, map, mergeMap, of, switchMap, toArray } from 'rxjs';
import { CapacityPlanningInputActions } from './capacity-planning-input.actions';


@Injectable()
export class CapacityPlanningInputEffects {

  getOrganizationalUnit$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.loadOrganizationalUnits),
        mergeMap(({ campaignId }) =>
          rs.getOrganizationalUnits(campaignId).pipe(
            mergeMap((organizationalUnits) => [
              CapacityPlanningInputActions.loadOrganizationalUnitsSuccess({ organizationalUnits })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.loadOrganizationalUnitsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  getOrganizationalUnitsComplete$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.loadOrganizationalUnitsComplete),
        mergeMap(({ campaignId, organizationalUnitSelected }) =>
          rs.getOrganizationalUnitsComplete(campaignId, organizationalUnitSelected).pipe(
            mergeMap((organizationalUnitsComplete) => [
              CapacityPlanningInputActions.loadOrganizationalUnitsCompleteSuccess({ organizationalUnitsComplete }),
            ]),
            catchError((error) => of(CapacityPlanningInputActions.loadOrganizationalUnitsCompleteFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  getLegalEntities$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.loadLegalEntities),
        mergeMap(({ campaignId, organizationalUnit }) =>
          rs.getLegalEntities(campaignId, organizationalUnit).pipe(
            mergeMap((legalEntities) => [
              CapacityPlanningInputActions.loadLegalEntitiesSuccess({ legalEntities })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.loadLegalEntitiesFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  getPeopleManagers$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.loadPeopleManagers),
        mergeMap(({ campaignId, legalEntity }) =>
          rs.getPeopleManagers(campaignId, legalEntity).pipe(
            mergeMap((peopleManagers) => [
              CapacityPlanningInputActions.loadPeopleManagersSuccess({ peopleManagers })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.loadPeopleManagersFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );



  // getAuditTeams$ = createEffect(
  //   (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
  //     return this.actions$.pipe(
  //       ofType(CapacityPlanningInputActions.loadAuditTeams),
  //       mergeMap(({ campaignId, legalEntity, peopleManagers }) =>
  //         rs.getAuditTeams(campaignId, legalEntity, peopleManagers).pipe(
  //           mergeMap((auditTeams) => [
  //             CapacityPlanningInputActions.loadAuditTeamsSuccess({ auditTeams }),
  //           ]),
  //           catchError((error) => of(CapacityPlanningInputActions.loadAuditTeamsFailure({ error })))
  //         )
  //       )
  //     );
  //   },
  //   { functional: true }
  // );

  getTeamMembers$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.loadTeamMembers),
        mergeMap(({ campaignId, legalEntityId, peopleManagerIds, capacityType }) =>
          rs.getTeamMember(campaignId, legalEntityId, peopleManagerIds, capacityType).pipe(
            mergeMap((teamMembers) => [
              CapacityPlanningInputActions.loadTeamMembersSuccess({ teamMembers }),
              CapacityPlanningInputActions.setLoadingState({ key: 'loadingTeamMembers', loading: false })
            ]),
            catchError((error) => of(
              CapacityPlanningInputActions.loadTeamMembersFailure({ error }),
              CapacityPlanningInputActions.setLoadingState({ key: 'loadingTeamMembers', loading: false })
            ))
          )
        )
      );
    },
    { functional: true }
  );

  getFirstForm$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.loadFirstForm),
        mergeMap(({ capacityPlanningId }) =>
          rs.getFirstForm(capacityPlanningId).pipe(
            mergeMap((firstForm) => [
              CapacityPlanningInputActions.loadFirstFormSuccess({ firstForm })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.loadFirstFormFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  createExternalFromUser$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.createExternalFromUser),
        mergeMap(({ campaignId, capacityType, legalEntityId, peopleManagerId, teamMemberId }) =>
          rs.createExternalFromUser(campaignId, teamMemberId, legalEntityId, peopleManagerId, capacityType).pipe(
            mergeMap((externals) => [
              CapacityPlanningInputActions.createExternalFromUserSuccess({ externals }),
              CapacityPlanningInputActions.getExternals({ campaignId, capacityType, legalEntityId, peopleManagerId })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.createExternalFromUserFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  getExternals$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.getExternals),
        mergeMap(({ campaignId, capacityType, legalEntityId, peopleManagerId }) =>
          rs.getExternals(campaignId, legalEntityId, peopleManagerId, capacityType).pipe(
            mergeMap((externals) => [
              CapacityPlanningInputActions.getExternalsSuccess({ externals })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.getExternalsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  deleteTeamMember$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.deleteTeamMember),
        mergeMap(({ capacityPlanningId }) =>
          rs.deleteTeamMember(capacityPlanningId).pipe(
            mergeMap((teamMemberDeleted) => [
              CapacityPlanningInputActions.deleteTeamMemberSuccess({ teamMemberDeleted })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.deleteTeamMemberFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  swapExternal$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.swapExternal),
        mergeMap(({ capacityPlanningId, newTeamMemberId, campaignId, legalEntityId, peopleManagerIds, capacityType }) =>
          rs.swapExternal(capacityPlanningId, newTeamMemberId).pipe(
            mergeMap((swapped) => [
              CapacityPlanningInputActions.swapExternalSuccess({ swapped }),
              CapacityPlanningInputActions.setLoadingState({ key: 'loadingTeamMembers', loading: true }),
              CapacityPlanningInputActions.loadTeamMembers({ campaignId, legalEntityId, peopleManagerIds, capacityType })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.swapExternalFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  renamePlaceholder$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.renamePlaceholder),
        mergeMap(({ capacityPlanningId, peopleManagerId, newTeamMemberName, galtMemberId }) =>
          rs.renamePlaceholder(capacityPlanningId, peopleManagerId, newTeamMemberName, galtMemberId).pipe(
            mergeMap((placeholder) => [
              CapacityPlanningInputActions.renamePlaceholderSuccess({ placeholder })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.renamePlaceholderFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  addPlaceholder$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.addPlaceholder),
        mergeMap(({ campaignId, capacityType, legalEntityId, peopleManagerId, teamMemberName, galtMemberId }) =>
          rs.addPlaceholder(campaignId, capacityType, legalEntityId, peopleManagerId, teamMemberName, galtMemberId).pipe(
            mergeMap((placeholder) => [
              CapacityPlanningInputActions.addPlaceholderSuccess({ placeholder })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.addPlaceholderFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadGaltMembers$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.loadGaltMembers),
        mergeMap(({ campaignId, organizationalUnitSelected }) =>
          rs.getGaltMembers(campaignId, organizationalUnitSelected).pipe(
            mergeMap((galtMembers) => [
              CapacityPlanningInputActions.loadGaltMembersSuccess({ galtMembers })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.loadGaltMembersFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateCapacityActivityAllocation$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.updateCapacityActivityAllocation),
        mergeMap(({ id, manDaysAllocation }) =>
          rs.updateCapacityActivityAllocation(id, manDaysAllocation).pipe(
            mergeMap((data) => [
              CapacityPlanningInputActions.updateCapacityActivityAllocationSuccess({ data })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.updateCapacityActivityAllocationFailure({ error, id })))
          )
        )
      );
    },
    { functional: true }
  );

  updateTeamMemberCapacityPlan$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.updateTeamMemberCapacityPlan),
        mergeMap(({ capacityPlanningId, startPeriodDate, endPeriodDate, totalAvailabilityInput, blockedPlanning, workContractAvailability }) =>
          rs.updateTeamMemberCapacityPlan(capacityPlanningId, startPeriodDate, endPeriodDate, totalAvailabilityInput, blockedPlanning, workContractAvailability).pipe(
            mergeMap((data) => [
              CapacityPlanningInputActions.updateTeamMemberCapacityPlanSuccess({ data })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.updateTeamMemberCapacityPlanFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  downloadCapacityPlanningExcel$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.downloadCapacityPlanningExcel),
        mergeMap(({ campaignId, legalEntityId, peopleManagerIds, capacityType }) =>
          rs.downloadCapacityPlanningExcel(campaignId, legalEntityId, peopleManagerIds, capacityType).pipe(
            mergeMap((data) => [
              CapacityPlanningInputActions.downloadCapacityPlanningExcelSuccess({ data })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.downloadCapacityPlanningExcelFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadCapacityActivityAllocations$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.loadCapacityActivityAllocations),
        mergeMap(({ capacityPlanningId }) =>
          rs.getCapacityActivityAllocations(capacityPlanningId).pipe(
            mergeMap((data) => [
              CapacityPlanningInputActions.loadCapacityActivityAllocationsSuccess({ data }),
              CapacityPlanningInputActions.setLoadingState({ key: 'loadingDynamicForms', loading: false })
            ]),
            catchError((error) => of(
              CapacityPlanningInputActions.loadCapacityActivityAllocationsFailure({ error }),
              CapacityPlanningInputActions.setLoadingState({ key: 'loadingDynamicForms', loading: false })
            ))
          )
        )
      );
    },
    { functional: true }
  );

  loadCapacityActivityAllocationSum$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.loadCapacityActivityAllocationSum),
        mergeMap(({ campaignId, legalEntityId, peopleManagerIds, capacityType }) =>
          rs.getCapacityActivityAllocationSum(campaignId, legalEntityId, peopleManagerIds, capacityType).pipe(
            mergeMap((data) => [
              CapacityPlanningInputActions.loadCapacityActivityAllocationSumSuccess({ data }),
              CapacityPlanningInputActions.setLoadingState({ key: 'loadingDynamicForms', loading: false })
            ]),
            catchError((error) => of(
              CapacityPlanningInputActions.loadCapacityActivityAllocationSumFailure({ error }),
              CapacityPlanningInputActions.setLoadingState({ key: 'loadingDynamicForms', loading: false })
            ))
          )
        )
      );
    },
    { functional: true }
  );

  loadLentedAllocations$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.loadLentedAllocations),
        mergeMap(({ capacityPlanningId }) =>
          rs.getLentedAllocations(capacityPlanningId).pipe(
            mergeMap((data) => [
              CapacityPlanningInputActions.loadLentedAllocationsSuccess({ data })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.loadLentedAllocationsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateLentedAllocation$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.updateLentedAllocation),
        mergeMap(({ allocationPlanningId, capacityPlanningId, activityValueChainId, organizationalUnit, legalEntityId, manDaysAllocation }) =>
          rs.updateLentedAllocation(allocationPlanningId, capacityPlanningId, activityValueChainId, organizationalUnit, legalEntityId, manDaysAllocation).pipe(
            mergeMap((data) => [
              CapacityPlanningInputActions.updateLentedAllocationSuccess({ data }),
              // Reload allocations after a successful single update
              CapacityPlanningInputActions.loadCapacityActivityAllocations({ capacityPlanningId })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.updateLentedAllocationFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  deleteLentedAllocation$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.deleteLentedAllocation),
        mergeMap(({ allocationPlanningId }) =>
          rs.deleteLentedAllocation(allocationPlanningId).pipe(
            mergeMap((data) => [
              CapacityPlanningInputActions.deleteLentedAllocationSuccess({ data })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.deleteLentedAllocationFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );


  allocationChanges$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.allocationChanges),
        mergeMap(({ planId, since }) =>
          rs.allocationChanges(planId, since).pipe(
            mergeMap((data) => [
              CapacityPlanningInputActions.allocationChangesSuccess({ data })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.allocationChangesFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  saveAllLentedAllocations$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.saveAllLentedAllocations),
        switchMap(({ rows }) =>
          from(rows).pipe(
            concatMap((row, index) =>
              rs.updateLentedAllocation(
                row.allocationPlanningId ?? null,
                row.capacityPlanningId,
                row.activityValueChainId,
                row.organizationalUnit,
                row.legalEntityId,
                row.manDaysAllocation
              ).pipe(
                map(() => ({ index, success: true, message: null as string | null })),
                catchError((error) => {
                  const message =
                    error?.graphQLErrors?.[0]?.message ||
                    error?.message ||
                    error?.networkError?.message ||
                    'Save failed';
                  return of({ index, success: false, message });
                })
              )
            ),
            toArray(),
            mergeMap((results) => {
              const failures = results
                .filter(r => !r.success)
                .map(r => ({ rowIndex: r.index, message: r.message as string }));

              if (failures.length > 0) {
                return [CapacityPlanningInputActions.saveAllLentedAllocationsFailure({ errors: failures })];
              }

              // All succeeded: reload allocations for the first row's capacityPlanningId
              const capacityPlanningId = rows[0]?.capacityPlanningId;
              return [
                CapacityPlanningInputActions.saveAllLentedAllocationsSuccess(),
                ...(capacityPlanningId
                  ? [CapacityPlanningInputActions.loadCapacityActivityAllocations({ capacityPlanningId })]
                  : [])
              ];
            })
          )
        )
      );
    },
    { functional: true }
  );

  loadPeopleManagersForExternalDialog$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.loadPeopleManagersForExternalDialog),
        mergeMap(({ campaignId, legalEntityId, peopleManagerId, capacityType, existing }) =>
          rs.getPeopleManagersForExternalDialog(campaignId, legalEntityId, peopleManagerId, capacityType, existing).pipe(
            mergeMap((peopleManagersForExternalDialog) => [
              CapacityPlanningInputActions.loadPeopleManagersForExternalDialogSuccess({ peopleManagersForExternalDialog })
            ]),
            catchError((error) => of(CapacityPlanningInputActions.loadPeopleManagersForExternalDialogFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  bulkSaveLentedAllocations$ = createEffect(
    (rs: CapacityPlanningInputService = inject(CapacityPlanningInputService)) => {
      return this.actions$.pipe(
        ofType(CapacityPlanningInputActions.bulkSaveLentedAllocations),
        switchMap(({ capacityPlanningId, upserts, deletes }) =>
          rs.bulkSaveLentedAllocations(upserts, deletes).pipe(
            mergeMap(({ upsertResults, deleteResults }) => {
              const upsertErrors = upsertResults
                .filter(r => !r.success)
                .map(r => ({ rowIndex: r.index, message: r.error ?? 'Save failed' }));
              const deleteErrors = deleteResults
                .filter(r => !r.success)
                .map(r => ({ id: r.id, message: r.error ?? 'Delete failed' }));

              if (upsertErrors.length > 0 || deleteErrors.length > 0) {
                return [CapacityPlanningInputActions.bulkSaveLentedAllocationsFailure({ upsertErrors, deleteErrors })];
              }

              return [
                CapacityPlanningInputActions.bulkSaveLentedAllocationsSuccess({ capacityPlanningId }),
                CapacityPlanningInputActions.loadCapacityActivityAllocations({ capacityPlanningId }),
              ];
            }),
            catchError((error) => of(
              CapacityPlanningInputActions.bulkSaveLentedAllocationsFailure({
                upsertErrors: [{ rowIndex: -1, message: error?.message || 'Unknown error' }],
                deleteErrors: [],
              })
            ))
          )
        )
      );
    },
    { functional: true }
  );

  constructor(private actions$: Actions) { }
}
